// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Truncate from "@bg/App/Components/Truncate";
import setQuickViewProduct from "@bg/App/Handlers/ProductQuickView/SetQuickViewProduct";
import { MonthlyDeal } from "@bg/App/Services/MonthlyDealsService";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { getQuickViewProductState } from "@bg/App/Store/Selectors/ProductQuickView/QuickViewSelector";
import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface Props extends Omit<MonthlyDeal, "unitOfMeasure" | "availableDate"> {
    extendedStyles?: MonthlyDealItemStyles;
}

const mapStateToProps = (state: CustomAppState, ownProps: Props) => ({
    isLoading: getQuickViewProductState(state).isLoading?.hasOwnProperty(ownProps.productId),
});

const mapDispatchToProps = {
    setQuickViewProduct,
};

interface MonthlyDealItemStyles {
    wrapper?: InjectableCss;
    productImageSectionWrapper?: InjectableCss;
    productDescription?: InjectableCss;
    brandImageWrapper?: InjectableCss;
    brandImage?: LazyImageProps;
    contentWrapper?: InjectableCss;
    productDetailsWrapper?: InjectableCss;
    productTitle?: TypographyPresentationProps;
    offerPrice?: TypographyPresentationProps;
    priceIcon?: IconPresentationProps;
    ribbon?: InjectableCss;
    ribbonContent?: InjectableCss;
    shopNow?: ButtonPresentationProps;
    shopNowText?: TypographyPresentationProps;
    productImageWrapper?: InjectableCss;
    productImage?: LazyImageProps;
    date?: InjectableCss;
    dateText?: TypographyPresentationProps;
    errorText?: LinkPresentationProps;
    selectButton?: ButtonPresentationProps;
    buttonWrapper?: InjectableCss;
}

export const monthlyDealItemStyles: MonthlyDealItemStyles = {
    wrapper: {
        css: css`
            position: relative;
            width: 636px;
            background-color: ${getColor("common.background")};
            box-shadow: 0 0 25px 0 rgba(79, 79, 119, 0.18);
            display: flex;
            @media screen and (max-width: 425px) {
                flex-direction: column-reverse;
            }
            @media screen and (max-width: 768px) {
                width: 480px;
                flex-direction: column-reverse;
            }
            @media screen and (max-width: 1329px) {
                flex-direction: column-reverse;
            }
        `,
    },
    productImageSectionWrapper: {
        css: css`
            width: 296px;
            @media screen and (max-width: 1329px) {
                width: 100%;
            }
        `,
    },
    productDescription: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    brandImageWrapper: {
        css: css`
            height: 50px;
            max-width: 150px;
            margin-bottom: 28px;
        `,
    },
    brandImage: {
        css: css`
            width: 100%;
            height: 100%;
        `,
    },
    contentWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            padding: 24px 12px 24px 30px;
            width: 340px;
            @media screen and (max-width: 1025px) {
                padding: 24px 12px 24px 0px;
                width: 100%;
                align-items: center;
            }
        `,
    },
    productDetailsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            @media screen and (max-width: 1329px) {
                width: 100%;
                padding: 24px 30px;
            }
        `,
    },
    productTitle: {
        variant: "h6",
        weight: fontConfig.weight.bold,
        color: "common.ui5",
    },
    offerPrice: {
        size: 22,
        lineHeight: fontConfig.lineHeight.singleLineSmall,
        weight: fontConfig.weight.semiBold,
        color: "common.white",
        css: css`
            margin-left: 3px;
        `,
    },
    priceIcon: {
        src: "LocalOffer",
        size: 16,
        color: "common.white",
        css: css`
            transform: rotate(90deg);
        `,
    },
    ribbon: {
        css: css`
            width: max-content;
            height: 29px;
            padding-left: 30px;
            margin-bottom: 10px;
            position: relative;
            left: -30px;
            background: ${getColor("common.ui6")};
            &:after {
                content: "";
                display: block;
                border: 13px solid transparent;
                border-right-color: white;
                position: absolute;
                right: -26px;
                top: 0px;
                height: 100%;
                background: ${getColor("common.ui6")};
            }
        `,
    },
    ribbonContent: {
        css: css`
            display: flex;
            color: ${getColor("common.white")};
            align-items: center;
            height: 100%;
        `,
    },
    shopNow: {
        sizeVariant: "small",
        variant: "primary",
        css: css`
            width: max-content;
            @media screen and (max-width: 1329px) {
                width: 100%;
            }
        `,
    },
    shopNowText: {
        size: fontConfig.fontSize.small,
        color: "text",
        weight: fontConfig.weight.semiBold,
        lineHeight: fontConfig.lineHeight.singleLineSmall,
        css: css`
            letter-spacing: 0.65px;
        `,
    },
    productImageWrapper: {
        css: css`
            height: 100%;
            width: 100%;
            position: relative;
            @media screen and (max-width: 1329px) {
                height: 336px;
                margin: 0 auto;
            }
        `,
    },
    productImage: {
        css: css`
            height: 100%;
        `,
    },
    date: {
        css: css`
            position: absolute;
            display: flex;
            justify-content: flex-end;
            top: 5px;
            right: 7px;
            z-index: 1;
            background: ${getColor("common.white")};
            padding: 9px 12px;
        `,
    },
    dateText: {
        color: "common.ui7",
        weight: fontConfig.weight.semiBold,
        lineHeight: fontConfig.lineHeight.small,
    },
    errorText: {
        typographyProps: {
            css: css`
                &&& {
                    color: ${getColor("primary")};
                    font-size: ${fontConfig.fontSize.regular}px;
                    font-weight: ${fontConfig.weight.semiBold};
                    line-height: ${fontConfig.lineHeight.singleLineSmall};
                    letter-spacing: 0.2px;
                }
            `,
        },
        css: css`
            margin-top: 14px;
            margin-bottom: 29px;
        `,
    },
    buttonWrapper: {
        css: css`
            z-index: 3;
            background-color: rgba(0, 0, 0, 0.75);
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            position: absolute;
            bottom: 0;
        `,
    },
    selectButton: {
        css: css`
            &&&& {
                background: transparent;
                width: 100%;

                &:hover,
                &:focus {
                    background: ${getColor("secondary.main")};
                    box-shadow: none;
                    border: 0;
                }
            }
        `,
        typographyProps: {
            color: "common.white",
        },
    },
};

type OwnProps = Props & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const MonthlyDealItem = ({
    availableDateDisplay,
    brandImage,
    productDescription,
    productImage,
    productTitle,
    pageUrl,
    product,
    productId,
    extendedStyles,
    setQuickViewProduct,
    isLoading,
}: OwnProps) => {
    const [styles] = React.useState(() => mergeToNew(monthlyDealItemStyles, extendedStyles));

    const setProductView = () => {
        setQuickViewProduct({
            productId,
        });
    };

    return (
        <StyledWrapper {...styles.wrapper} aria-label={productTitle} className="product-card">
            <StyledWrapper {...styles.contentWrapper}>
                <StyledWrapper {...styles.productDetailsWrapper}>
                    <StyledWrapper {...styles.brandImageWrapper}>
                        <LazyImage
                            altText={productTitle}
                            src={brandImage}
                            {...styles.brandImage}
                            cloudinaryWidth="150"
                        />
                    </StyledWrapper>
                    <Typography {...styles.productTitle}>
                        <Truncate lines={2}>{productTitle}</Truncate>
                    </Typography>
                    <StyledWrapper {...styles.ribbon}>
                        <StyledWrapper {...styles.ribbonContent}>
                            <Icon {...styles.priceIcon} />
                            <ProductPrice
                                product={product}
                                {...({ monthlyDeals: true } as any)}
                                extendedStyles={{
                                    wrapper: {
                                        css: css`
                                            display: flex;
                                            flex-direction: column;
                                        `,
                                    },
                                    errorWrapper: {
                                        css: css`
                                            width: 85px;
                                            padding-left: 10px;
                                            &&& > * {
                                                color: ${getColor("common.white")};
                                            }
                                        `,
                                    },
                                }}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.productDescription}>
                        <Typography>
                            <Truncate lines={7} lineHeight={18}>
                                {parse(productDescription, parserOptions)}
                            </Truncate>
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
                <Button {...({ href: pageUrl } as any)} {...styles.shopNow}>
                    <Typography {...styles.shopNowText}>{translate("Shop Now")}</Typography>
                </Button>
            </StyledWrapper>
            <StyledWrapper {...styles.productImageSectionWrapper}>
                <StyledWrapper {...styles.date}>
                    <Typography {...styles.dateText}>{availableDateDisplay || "June 2021"}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.productImageWrapper}>
                    <LazyImage
                        altText={productTitle}
                        src={productImage}
                        {...styles.productImage}
                        cloudinaryWidth="296"
                        cloudinaryHeight="296"
                    />
                    <StyledWrapper className="quick-view" {...styles.buttonWrapper}>
                        <Button onClick={setProductView} {...styles.selectButton}>
                            {isLoading === true ? (
                                <LoadingSpinner size={13} color="common.white" />
                            ) : (
                                translate("Quick View")
                            )}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MonthlyDealItem);
